import { useEffect, useState } from "react";
import type {
  GroupSummary,
  MeResponse,
  MiniAppDraft,
  MiniAppTemplate,
  PersonalButtonSet,
  TargetHealth
} from "../../api/client";
import { openTelegramLink as defaultOpenTelegramLink } from "../../telegram/webapp";
import { ButtonSetsScreen, type ButtonSetsApi } from "../button-sets/ButtonSetsScreen";
import type { ButtonLayoutLimits } from "../composer/button-layout-model";
import {
  AdminCategorySyncScreen,
  type CategorySyncApi
} from "./AdminCategorySyncScreen";

const reasonLabels: Record<string, string> = {
  chat_not_found: "Группа недоступна",
  bot_kicked: "Бот удалён из группы",
  bot_not_member: "Бот не состоит в группе",
  missing_rights: "Недостаточно прав",
  user_blocked: "Бот заблокирован"
};

type MoreView = "root" | "groups" | "drafts" | "templates" | "buttonSets" | "categorySync";

type MoreScreenProps = {
  capabilities: MeResponse["capabilities"];
  buttonLimits: ButtonLayoutLimits;
  loadGroups: () => Promise<GroupSummary[]>;
  loadDrafts: () => Promise<MiniAppDraft[]>;
  loadTemplates: () => Promise<MiniAppTemplate[]>;
  loadButtonSets: () => Promise<PersonalButtonSet[]>;
  buttonSetsApi: ButtonSetsApi;
  categorySyncApi: CategorySyncApi;
  checkGroup: (chatId: number) => Promise<{ item: TargetHealth; requiresEnable: boolean }>;
  enableGroup: (chatId: number) => Promise<{ item: TargetHealth; reenabled: boolean }>;
  openBotLink?: (url: string) => void;
  onOpenDeliveries: () => void;
};

const formatUpdatedAt = (value: string) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "Дата не указана"
    : new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }).format(parsed);
};

const draftSummary = (draft: MiniAppDraft) => {
  const candidate = draft.payload.text ?? draft.payload.title;
  return typeof candidate === "string" && candidate.trim()
    ? candidate.trim().slice(0, 180)
    : draft.kind === "price" ? "Черновик прайс-поста" : "Черновик своего поста";
};

export function MoreScreen({
  capabilities,
  buttonLimits,
  loadGroups,
  loadDrafts,
  loadTemplates,
  loadButtonSets,
  buttonSetsApi,
  categorySyncApi,
  checkGroup,
  enableGroup,
  openBotLink = defaultOpenTelegramLink,
  onOpenDeliveries
}: MoreScreenProps) {
  const [view, setView] = useState<MoreView>("root");
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [drafts, setDrafts] = useState<MiniAppDraft[]>([]);
  const [templates, setTemplates] = useState<MiniAppTemplate[]>([]);
  const [buttonSets, setButtonSets] = useState<PersonalButtonSet[]>([]);
  const [pending, setPending] = useState<number | null>(null);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void Promise.all([loadGroups(), loadDrafts(), loadTemplates(), loadButtonSets()]).then(
      ([nextGroups, nextDrafts, nextTemplates, nextButtonSets]) => {
        if (!active) return;
        setGroups(nextGroups);
        setDrafts(nextDrafts);
        setTemplates(nextTemplates);
        setButtonSets(nextButtonSets);
      },
      () => { if (active) setError("Не удалось загрузить часть раздела. Попробуйте открыть его снова."); }
    );
    return () => { active = false; };
  }, [loadGroups, loadDrafts, loadTemplates, loadButtonSets]);

  const runCheck = async (chatId: number) => {
    setPending(chatId);
    setError(null);
    try {
      await checkGroup(chatId);
      setChecked((current) => new Set(current).add(chatId));
      setGroups((current) => current.map((group) => group.chatId === chatId ? { ...group, healthReasonCode: null } : group));
    } catch {
      setError("Проверка не пройдена. Убедитесь, что бот добавлен в группу и имеет право публиковать.");
    } finally {
      setPending(null);
    }
  };

  const runEnable = async (chatId: number) => {
    setPending(chatId);
    setError(null);
    try {
      await enableGroup(chatId);
      setChecked((current) => {
        const next = new Set(current);
        next.delete(chatId);
        return next;
      });
    } catch {
      setError("Не удалось включить публикации. Повторите проверку группы.");
    } finally {
      setPending(null);
    }
  };

  const subviewHeader = (title: string, eyebrow: string) => <header className="composer-header">
    <button type="button" aria-label="Назад в раздел Ещё" onClick={() => setView("root")}>←</button>
    <div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1></div>
    <span />
  </header>;

  const groupsPanel = <section className="group-health">
    {error && <p className="inline-error" role="alert">{error}</p>}
    {groups.length === 0 ? <p className="helper-copy">Добавьте бота в группу и зарегистрируйте её через меню бота.</p> : <div className="group-health-list">{groups.map((group) => {
      const unhealthy = Boolean(group.healthReasonCode);
      const readyToEnable = checked.has(group.chatId);
      return <article key={group.chatId} className={unhealthy ? "unhealthy" : ""}><div><strong>{group.title || "Группа без названия"}</strong><small>{unhealthy ? reasonLabels[group.healthReasonCode ?? ""] || "Требуется проверка" : readyToEnable ? "Проверка пройдена — публикации ещё выключены" : "Готова к публикации"}</small></div>{unhealthy ? <button type="button" disabled={pending === group.chatId} onClick={() => void runCheck(group.chatId)}>{pending === group.chatId ? "Проверяем…" : "Проверить"}</button> : readyToEnable ? <button className="enable" type="button" disabled={pending === group.chatId} onClick={() => void runEnable(group.chatId)}>{pending === group.chatId ? "Включаем…" : "Включить"}</button> : <span className="mini-status-ok">✓</span>}</article>;
    })}</div>}
  </section>;

  if (view === "buttonSets") {
    return <ButtonSetsScreen api={buttonSetsApi} limits={buttonLimits} onClose={() => setView("root")} />;
  }
  if (view === "categorySync" && capabilities.admin) {
    return <AdminCategorySyncScreen api={categorySyncApi} onClose={() => setView("root")} />;
  }
  if (view === "groups") {
    return <section className="screen-stack section-screen">{subviewHeader("Группы", "Куда публикуем")}{groupsPanel}</section>;
  }
  if (view === "drafts") {
    return <section className="screen-stack section-screen">{subviewHeader("Черновики", "Незавершённые публикации")}{drafts.length === 0 ? <div className="empty-state"><strong>Черновиков нет</strong><p>Незавершённые публикации появятся здесь.</p></div> : <div className="more-subview-list">{drafts.map((draft) => <article key={draft.id}><strong>{draftSummary(draft)}</strong><small>{draft.kind === "price" ? "Прайс-пост" : "Свой пост"} · {formatUpdatedAt(draft.updatedAt)}</small></article>)}</div>}</section>;
  }
  if (view === "templates") {
    return <section className="screen-stack section-screen">{subviewHeader("Шаблоны", "Быстрое создание")}{templates.length === 0 ? <div className="empty-state"><strong>Шаблонов нет</strong><p>Сохранённые шаблоны появятся здесь.</p></div> : <div className="more-subview-list">{templates.map((template) => <article key={template.id}><strong>{template.name}</strong><small>{template.kind === "price" ? "Прайс-пост" : "Свой пост"} · {formatUpdatedAt(template.updatedAt)}</small></article>)}</div>}</section>;
  }

  const rows = [
    { title: "Группы", value: String(groups.length), subtitle: "Куда можно публиковать", action: () => setView("groups") },
    { title: "Черновики", value: String(drafts.length), subtitle: "Незавершённые публикации", action: () => setView("drafts") },
    { title: "Шаблоны", value: String(templates.length), subtitle: "Быстрое создание поста", action: () => setView("templates") },
    { title: "Мои наборы кнопок", value: String(buttonSets.length), subtitle: "Личные кнопки для ваших постов", action: () => setView("buttonSets") },
    { title: "PDF-прайс", value: "↗", subtitle: "Получить в чате с ботом", action: () => openBotLink("https://t.me/THLS_AllPrice_bot?start=pdf") }
  ];

  return <section className="screen-stack section-screen">
    <header><p className="eyebrow">Настройки и материалы</p><h1>Ещё</h1></header>
    {error && <p className="inline-error" role="alert">{error}</p>}
    <div className="menu-list">{rows.map(({ title, value, subtitle, action }) => <button type="button" key={title} onClick={action}><span><strong>{title}</strong><small>{subtitle}</small></span><b>{value}</b></button>)}</div>
    {capabilities.admin && <section className="admin-panel"><p className="eyebrow">Только админ</p><h2>Управление THLS</h2><div className="admin-actions"><button type="button" onClick={() => setView("categorySync")}>Синхронизация категорий</button><button type="button" onClick={onOpenDeliveries}>MAX</button><button type="button" onClick={() => openBotLink("https://t.me/THLS_AllPrice_bot?start=admin_categories")}>Категории и флаги</button><button type="button" onClick={() => openBotLink("https://t.me/THLS_AllPrice_bot?start=admin_stats")}>Статистика</button></div></section>}
  </section>;
}
